import React from "react";
import Modal from "react-modal";
import { useState, useEffect, useMemo, useRef } from "react";
import jwtDecode from "jwt-decode";
import { useTheme, getCssVar } from "../../hooks/useTheme";

const toDirectImageUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const m = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) || url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (m) return `https://drive.google.com/uc?export=view&id=${m[1]}`;
  return url;
};

const toPdfPreviewUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  if (url.includes("drive.google.com") || /^[a-zA-Z0-9_-]{20,}$/.test(url)) {
    const m = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) || url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
    const id = m ? m[1] : url;
    return `https://drive.google.com/file/d/${id}/preview`;
  }
  return url;
};

const API = "https://api.fullagro.com";

const proxyImageUrl = (product, type) => {
  const id = product && (product["Alpha_Batch_Id"] || product["Fullagro_Batch_Id"]);
  if (!id) return "";
  return `${API}/image-proxy?alpha=${encodeURIComponent(id)}&type=${type}`;
};

const CloseIcon = ({ size = 22, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const DownloadIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const CartIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
  </svg>
);

export default function ProductInfoModal({
  isOpen,
  onRequestClose,
  product,
  onAddToOrder,
  productData,
  onSelectBatch,
}) {
  const theme = useTheme();
  const isLight = theme === "light";

  const palette = useMemo(() => ({
    bg: getCssVar("--fa-bg", isLight ? "#f6f9f5" : "#0a0e0a"),
    bgSurface: getCssVar("--fa-bg-elevated", isLight ? "#ffffff" : "#0d130d"),
    bgCard: getCssVar("--fa-bg-sunken", isLight ? "#eef3ec" : "#0e130e"),
    border: getCssVar("--fa-border", isLight ? "#cfd8cf" : "#2a3a2a"),
    borderStrong: isLight ? "#b5c4b5" : "#3a4a3a",
    text: getCssVar("--fa-text", isLight ? "#0a0e0a" : "#e5ede5"),
    textMuted: getCssVar("--fa-text-muted", isLight ? "#5a6a5a" : "#9aa89a"),
    textSubtle: isLight ? "#7a8a7a" : "#7d8a7d",
    accent: getCssVar("--fa-accent", isLight ? "#2e7d32" : "#7ee787"),
    accentFg: getCssVar("--fa-accent-fg", isLight ? "#ffffff" : "#0a0e0a"),
    trackBg: isLight ? "#dde6dc" : "#1c271c",
    inputBg: isLight ? "#ffffff" : "#0e130e",
    barTrack: isLight ? "#dde6dc" : "#1c271c",
    shadow: isLight ? "0 12px 32px rgba(0,0,0,0.18)" : "0 12px 32px rgba(0,0,0,0.6)",
  }), [isLight]);

  const [quantity, setQuantity] = useState("");
  const [matchingProducts, setMatchingProducts] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [plantImageSrc, setPlantImageSrc] = useState("");
  const [seedImageSrc, setSeedImageSrc] = useState("");
  const [imgError, setImgError] = useState({ plant: false, seed: false });
  const [lightbox, setLightbox] = useState(null);
  const [dataSheetOpen, setDataSheetOpen] = useState(false);
  const blobUrlsRef = useRef([]);

  useEffect(() => {
    if (isOpen) {
      setQuantity("");
      setImgError({ plant: false, seed: false });

      const token = localStorage.getItem("token");
      let admin = false;
      try {
        const decoded = token ? jwtDecode(token) : null;
        admin = !!(decoded && decoded.user_type === "admin");
      } catch (e) {
        admin = false;
      }
      setIsAdmin(admin);

      let matches = productData.filter((p) => p.Strain === product.Strain);
      if (!admin) {
        matches = matches.filter(
          (p) => (Number(p.Europe) || 0) > 0 || (Number(p.US_Canada) || 0) > 0
        );
      }
      setMatchingProducts(matches);

      const loadImage = async (type) => {
        const id = product["Alpha_Batch_Id"] || product["Fullagro_Batch_Id"];
        if (!id) return;
        try {
          const res = await fetch(`${API}/image-proxy?alpha=${encodeURIComponent(id)}&type=${type}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return;
          const blob = await res.blob();
          const blobUrl = URL.createObjectURL(blob);
          blobUrlsRef.current.push(blobUrl);
          if (type === "plant") setPlantImageSrc(blobUrl);
          else setSeedImageSrc(blobUrl);
        } catch (e) {
          console.error("image load failed:", e);
        }
      };
      loadImage("plant");
      loadImage("seed");
    } else {
      setSeedImageSrc("");
      setPlantImageSrc("");
      setMatchingProducts([]);
      setIsAdmin(false);
      blobUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      blobUrlsRef.current = [];
    }
  }, [isOpen, productData, product]);

  const handleAddToOrder = () => {
    if (!quantity || parseInt(quantity, 10) <= 0) return;
    onAddToOrder({ ...product, quantity });
    onRequestClose();
  };

  const styles = useMemo(() => buildStyles(palette), [palette]);

  if (!product) return null;

  const batchIdDisplay = product["Alpha_Batch_Id"] || product["Fullagro_Batch_Id"] || "—";
  const internalHash = product["Fullagro_Batch_Id"] || "—";

  const hermieScore = product["Alphatype_Hermie_Score"] != null && product["Alphatype_Hermie_Score"] !== ""
    ? Number(product["Alphatype_Hermie_Score"])
    : null;

  const scoreBar = (label, value, max, color, mode = "percent") => {
    const num = Number(value);
    if (value == null || value === "" || isNaN(num)) {
      return (
        <div style={styles.barWrap}>
          <div style={styles.barLabel}>
            <span style={{ color: palette.textSubtle }}>{label}</span>
          </div>
          <div style={styles.barEmpty}>Not available</div>
        </div>
      );
    }
    const pct = Math.min(100, Math.max(0, (num / max) * 100));
    const isHermie = label.toLowerCase().includes("hermaphroditism");
    const fillColor = isHermie
      ? (pct < 30 ? (isLight ? "#2e7d32" : "#7ee787") : pct < 60 ? (isLight ? "#c79a30" : "#e8c87a") : (isLight ? "#b3382e" : "#e87a7a"))
      : color;

    let valueNode = null;
    let footerNode = null;

    if (mode === "low_med_high") {
      valueNode = null;
      footerNode = (
        <div style={styles.barLabels3}>
          <span style={styles.barLabelLeft}>Low</span>
          <span style={styles.barLabelMid}>Medium</span>
          <span style={styles.barLabelRight}>High</span>
        </div>
      );
    } else if (mode === "weeks") {
      valueNode = null;
      footerNode = (
        <div style={styles.barLabelsWeeks}>
          <span style={styles.barLabelLeft}>6</span>
          <span style={styles.barLabelRight}>10</span>
        </div>
      );
    } else if (mode === "hermaphroditism") {
      const formatted = Number.isInteger(num) ? `${num}%` : `${num.toFixed(2)}%`;
      valueNode = <span style={styles.barValue}>{formatted}</span>;
    } else {
      const suffix = typeof mode === "string" ? mode : "";
      valueNode = <span style={styles.barValue}>{num.toFixed(2)}{suffix}</span>;
    }

    return (
      <div style={styles.barWrap}>
        <div style={styles.barLabel}>
          <span style={{ color: palette.textMuted }}>{label}</span>
          {valueNode}
        </div>
        <div style={styles.barTrack}>
          <div style={{ ...styles.barFill, width: `${pct}%`, background: fillColor }} />
        </div>
        {footerNode}
      </div>
    );
  };

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={{
        overlay: {
          backgroundColor: isLight ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.65)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          zIndex: 1000,
        },
        content: {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          marginRight: "-50%",
          transform: "translate(-50%, -50%)",
          width: "min(1100px, 95vw)",
          maxHeight: "92vh",
          background: palette.bgSurface,
          border: `1px solid ${palette.border}`,
          borderRadius: 16,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          color: palette.text,
          fontFamily: "inherit",
          boxShadow: palette.shadow,
        },
      }}
      contentLabel="Product Information"
    >
      {/* HEADER */}
      <div style={styles.header}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.strainLabel}>Strain</div>
          <div style={styles.strainName}>{product.Strain || "—"}</div>
        </div>
        <button style={styles.closeBtn} onClick={onRequestClose} aria-label="Close">
          <CloseIcon />
        </button>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div style={styles.body}>
        {/* Quick info chips */}
        <div style={styles.chipsRow}>
          <div style={styles.chip}>
            <span style={styles.chipLabel}>Batch ID</span>
            <span style={styles.chipValue}>{batchIdDisplay}</span>
          </div>
          <div style={styles.chip}>
            <span style={styles.chipLabel}>Cycle</span>
            <span style={styles.chipValue}>{product.Cycle || "—"}</span>
          </div>
          <div style={styles.chip}>
            <span style={styles.chipLabel}>Type</span>
            <span style={styles.chipValue}>
              {product.Source || "—"}{product["Source_Percetage"] ? ` (${product["Source_Percetage"]}%)` : ""}
            </span>
          </div>
          <div style={styles.chip}>
            <span style={styles.chipLabel}>Harvest</span>
            <span style={styles.chipValue}>{product.Harvest || "—"}</span>
          </div>
        </div>

        {/* Images */}
        <div style={styles.imagesGrid}>
          <div style={styles.imageCard}>
            <div style={styles.imageLabel}>Plant</div>
            {plantImageSrc && !imgError.plant ? (
              <button
                style={styles.imageBtn}
                onClick={() => setLightbox({ src: plantImageSrc, label: `${product.Strain} · Plant` })}
                title="Click to enlarge"
              >
                <img
                  src={plantImageSrc}
                  alt="Plant"
                  style={styles.image}
                  onError={() => setImgError(e => ({ ...e, plant: true }))}
                />
                <span style={styles.imageZoomIcon}>⛶</span>
                {internalHash !== "—" && (
                  <div style={styles.imageLotBand}>
                    <span style={styles.imageLotBandText}>LOT {internalHash}</span>
                  </div>
                )}
              </button>
            ) : (
              <div style={styles.imagePlaceholder}>No image</div>
            )}
          </div>
          <div style={styles.imageCard}>
            <div style={styles.imageLabel}>Seed</div>
            {seedImageSrc && !imgError.seed ? (
              <button
                style={styles.imageBtn}
                onClick={() => setLightbox({ src: seedImageSrc, label: `${product.Strain} · Seed` })}
                title="Click to enlarge"
              >
                <img
                  src={seedImageSrc}
                  alt="Seed"
                  style={styles.image}
                  onError={() => setImgError(e => ({ ...e, seed: true }))}
                />
                <span style={styles.imageZoomIcon}>⛶</span>
                {internalHash !== "—" && (
                  <div style={styles.imageLotBand}>
                    <span style={styles.imageLotBandText}>LOT {internalHash}</span>
                  </div>
                )}
              </button>
            ) : (
              <div style={styles.imagePlaceholder}>No image</div>
            )}
          </div>
        </div>

        {/* Quality bars */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Quality Metrics</div>
          <div style={styles.barsGrid}>
            {scoreBar("THC", product["THC_Value"], 30, palette.accent, "percent")}
            {scoreBar("Dry Flower Yield", product["Dry_Flower_Indoor_Yield"], 100, isLight ? "#1d6fc9" : "#7eb8f5", "low_med_high")}
            {scoreBar("Flavor Cluster", product["Flavor_Cluster"], 100, isLight ? "#b81f9a" : "#e87ad6", "low_med_high")}
            {scoreBar("Flowering Time", product["Flowering_Time_Percentage"], 100, isLight ? "#b8921b" : "#f5d77e", "weeks")}
            {scoreBar("Hermaphroditism", hermieScore, 0.05, isLight ? "#b3382e" : "#e87a7a", "hermaphroditism")}
          </div>
        </div>

        {/* Flavor profile */}
        {product["Flavor_Aroma_Profile"] && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Flavor / Aroma Profile</div>
            <div style={styles.flavorBox}>
              {product["Flavor_Aroma_Profile"]
                .split("\n")
                .map((line, i) => line.trim())
                .filter(Boolean)
                .map((line, i) => (
                  <div key={i} style={styles.flavorItem}>{line}</div>
                ))}
            </div>
          </div>
        )}

        {/* Batch History */}
        {matchingProducts.length > 0 && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              Batch History <span style={styles.muted}>({matchingProducts.length} records)</span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Batch ID</th>
                    <th style={styles.th}>Cycle</th>
                    <th style={styles.th}>Harvest</th>
                    <th style={styles.th}>THC %</th>
                    <th style={styles.th}>Europe</th>
                    <th style={styles.th}>US</th>
                  </tr>
                </thead>
                <tbody>
                  {matchingProducts.map((m, i) => {
                    const isActive =
                      (product["Alpha_Batch_Id"] && m["Alpha_Batch_Id"] === product["Alpha_Batch_Id"]) ||
                      (!product["Alpha_Batch_Id"] && m["Fullagro_Batch_Id"] === product["Fullagro_Batch_Id"]);
                    return (
                      <tr
                        key={i}
                        style={{
                          ...styles.tableRow,
                          ...(onSelectBatch ? styles.tableRowClickable : null),
                          ...(isActive ? styles.tableRowActive : null),
                        }}
                        onClick={onSelectBatch ? () => onSelectBatch(m) : undefined}
                        title={onSelectBatch ? "Click to view this batch" : undefined}
                      >
                        <td style={styles.td}>{m["Alpha_Batch_Id"] || m["Fullagro_Batch_Id"] || "—"}</td>
                        <td style={styles.td}>{m.Cycle || "—"}</td>
                        <td style={styles.td}>{m.Harvest || "—"}</td>
                        <td style={styles.tdNum}>{m["THC_Value"] || "—"}</td>
                        <td style={styles.tdNum}>{m["Europe"] || 0}</td>
                        <td style={styles.tdNum}>{m["US_Canada"] || 0}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Germination PDF */}
        {product["Germination_Test_Link_PDF"] && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>
              Germination Test
              {product["Germination_Test"] && (
                <span style={styles.muted}> · {product["Germination_Test"]}</span>
              )}
            </div>
            {internalHash !== "—" && (
              <div style={styles.dataSheetBand}>
                <span style={styles.dataSheetBandLabel}>INTERNAL LOT</span>
                <span style={styles.dataSheetBandValue}>{internalHash}</span>
              </div>
            )}
            <div style={styles.pdfRow}>
              <button
                type="button"
                style={styles.pdfBtn}
                onClick={() => setDataSheetOpen(true)}
              >
                <DownloadIcon />
                <span>View Data Sheet</span>
              </button>
              <a
                href={product["Germination_Test_Link_PDF"]}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.pdfBtn}
              >
                <DownloadIcon />
                <span>Open Original</span>
              </a>
            </div>
          </div>
        )}

        {/* Internal */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Internal</div>
          <div style={styles.helperText}>
            Internal ID: <code style={styles.code}>{internalHash}</code>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div style={styles.footer}>
        <input
          type="number"
          min="1"
          placeholder="Quantity"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          style={styles.qtyInput}
          onKeyDown={(e) => { if (e.key === "Enter") handleAddToOrder(); }}
        />
        <button
          onClick={handleAddToOrder}
          style={{
            ...styles.addBtn,
            opacity: quantity && parseInt(quantity, 10) > 0 ? 1 : 0.5,
            cursor: quantity && parseInt(quantity, 10) > 0 ? "pointer" : "not-allowed",
          }}
          disabled={!quantity || parseInt(quantity, 10) <= 0}
        >
          <CartIcon />
          <span>Add to Order</span>
        </button>
      </div>

      {/* DATA SHEET VIEWER */}
      {dataSheetOpen && (
        <div style={styles.dataSheetOverlay} onClick={() => setDataSheetOpen(false)}>
          <button
            style={styles.dataSheetClose}
            onClick={() => setDataSheetOpen(false)}
            aria-label="Close data sheet"
          >
            <CloseIcon size={28} />
          </button>
          {internalHash !== "—" && (
            <div style={styles.dataSheetOverlayBand}>
              <span style={styles.dataSheetOverlayBandLabel}>INTERNAL LOT</span>
              <span style={styles.dataSheetOverlayBandValue}>{internalHash}</span>
            </div>
          )}
          <iframe
            title="Data sheet"
            src={toPdfPreviewUrl(product["Germination_Test_Link_PDF"])}
            style={styles.dataSheetIframe}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* LIGHTBOX */}
      {lightbox && (
        <div style={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
          <button style={styles.lightboxClose} onClick={() => setLightbox(null)} aria-label="Close">
            <CloseIcon size={28} />
          </button>
          <div style={styles.lightboxLabel}>{lightbox.label}</div>
          <img
            src={lightbox.src}
            alt={lightbox.label}
            style={styles.lightboxImage}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Modal>
  );
}

function buildStyles(p) {
  return {
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "1rem 1.5rem",
      borderBottom: `1px solid ${p.border}`,
      background: p.bg,
      flexShrink: 0,
    },
    strainLabel: {
      fontSize: 11,
      color: p.textSubtle,
      textTransform: "uppercase",
      letterSpacing: 1.2,
      fontWeight: 600,
      marginBottom: 4,
    },
    strainName: {
      fontSize: 22,
      fontWeight: 700,
      color: p.text,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    closeBtn: {
      background: "transparent",
      border: `1px solid ${p.border}`,
      borderRadius: 8,
      color: p.textMuted,
      width: 36,
      height: 36,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
      marginLeft: 12,
    },
    body: {
      flex: 1,
      overflowY: "auto",
      padding: "1.5rem",
      background: p.bgSurface,
    },
    chipsRow: {
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      marginBottom: 20,
    },
    chip: {
      flex: "1 1 auto",
      minWidth: 120,
      padding: "10px 14px",
      background: p.bgCard,
      border: `1px solid ${p.border}`,
      borderRadius: 10,
      display: "flex",
      flexDirection: "column",
      gap: 2,
    },
    chipLabel: {
      fontSize: 10,
      color: p.textSubtle,
      textTransform: "uppercase",
      letterSpacing: 0.8,
      fontWeight: 600,
    },
    chipValue: {
      fontSize: 14,
      color: p.text,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    imagesGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      marginBottom: 16,
    },
    imageCard: {
      background: p.bgCard,
      border: `1px solid ${p.border}`,
      borderRadius: 12,
      padding: 12,
      minHeight: 320,
      display: "flex",
      flexDirection: "column",
    },
    imageLabel: {
      fontSize: 11,
      color: p.textSubtle,
      textTransform: "uppercase",
      letterSpacing: 0.8,
      fontWeight: 600,
      marginBottom: 8,
    },
    image: {
      width: "100%",
      flex: 1,
      objectFit: "contain",
      borderRadius: 8,
      background: "#000",
      minHeight: 280,
      maxHeight: 360,
    },
    imageBtn: {
      position: "relative",
      flex: 1,
      padding: 0,
      border: "none",
      background: "transparent",
      cursor: "zoom-in",
      borderRadius: 8,
      overflow: "hidden",
      display: "flex",
      width: "100%",
      minHeight: 280,
      maxHeight: 360,
    },
    imageZoomIcon: {
      position: "absolute",
      top: 6,
      right: 6,
      width: 24,
      height: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.55)",
      color: "#fff",
      borderRadius: 4,
      fontSize: 14,
      lineHeight: 1,
      pointerEvents: "none",
      opacity: 0.85,
    },
    lightboxOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.92)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      zIndex: 2000,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 32,
      cursor: "zoom-out",
    },
    lightboxClose: {
      position: "absolute",
      top: 16,
      right: 16,
      width: 44,
      height: 44,
      background: "rgba(255,255,255,0.1)",
      border: "1px solid rgba(255,255,255,0.2)",
      color: "#fff",
      borderRadius: 10,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    lightboxLabel: {
      color: "#b6e0b6",
      fontSize: 13,
      fontWeight: 600,
      marginBottom: 12,
      letterSpacing: 0.5,
    },
    lightboxImage: {
      maxWidth: "92vw",
      maxHeight: "85vh",
      objectFit: "contain",
      borderRadius: 8,
      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      cursor: "default",
    },
    imagePlaceholder: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: p.textSubtle,
      fontSize: 13,
      background: p.bg,
      borderRadius: 8,
      border: `1px dashed ${p.border}`,
      minHeight: 280,
      maxHeight: 360,
    },
    imageLotBand: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(0,0,0,0.85)",
      color: "#fff",
      padding: "6px 10px",
      fontSize: 11,
      letterSpacing: 0.6,
      fontWeight: 700,
      fontFamily: "monospace",
      textAlign: "center",
      pointerEvents: "none",
    },
    imageLotBandText: {
      color: "#fff",
    },
    section: {
      marginBottom: 20,
    },
    sectionTitle: {
      fontSize: 12,
      color: p.textSubtle,
      textTransform: "uppercase",
      letterSpacing: 1,
      fontWeight: 600,
      marginBottom: 12,
    },
    barsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 14,
    },
    barWrap: {
      background: p.bgCard,
      border: `1px solid ${p.border}`,
      borderRadius: 10,
      padding: "10px 14px",
    },
    barLabel: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontSize: 12,
      color: p.textMuted,
      marginBottom: 6,
      fontWeight: 500,
    },
    barValue: {
      color: p.text,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      fontSize: 13,
    },
    barTrack: {
      height: 6,
      background: p.barTrack,
      borderRadius: 3,
      overflow: "hidden",
    },
    barFill: {
      height: "100%",
      borderRadius: 3,
      transition: "width 220ms ease",
    },
    barEmpty: {
      fontSize: 11,
      color: p.textSubtle,
      fontStyle: "italic",
    },
    barLabels3: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: 6,
      fontSize: 10,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      color: p.textSubtle,
      fontWeight: 600,
    },
    barLabelsWeeks: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginTop: 6,
      fontSize: 11,
      fontWeight: 700,
      color: p.textMuted,
      fontVariantNumeric: "tabular-nums",
    },
    barLabelLeft: {
      textAlign: "left",
    },
    barLabelMid: {
      textAlign: "center",
    },
    barLabelRight: {
      textAlign: "right",
    },
    flavorBox: {
      background: p.bgCard,
      border: `1px solid ${p.border}`,
      borderRadius: 10,
      padding: "12px 16px",
      fontSize: 13,
      lineHeight: 1.7,
    },
    flavorItem: {
      color: p.accent,
    },
    helperText: {
      fontSize: 12,
      color: p.textSubtle,
      marginTop: 8,
      fontStyle: "italic",
    },
    muted: {
      color: p.textSubtle,
      fontWeight: 400,
      marginLeft: 4,
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 13,
      background: p.bgCard,
      borderRadius: 10,
      overflow: "hidden",
    },
    th: {
      textAlign: "left",
      padding: "10px 14px",
      background: p.bg,
      color: p.textSubtle,
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: 0.8,
      fontWeight: 600,
      borderBottom: `1px solid ${p.border}`,
    },
    td: {
      padding: "10px 14px",
      color: p.text,
      borderBottom: `1px solid ${p.border}`,
    },
    tdNum: {
      padding: "10px 14px",
      color: p.text,
      borderBottom: `1px solid ${p.border}`,
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
    },
    tableRow: {
      transition: "background 120ms",
    },
    tableRowClickable: {
      cursor: "pointer",
    },
    tableRowActive: {
      background: p.accent,
    },
    code: {
      background: p.bg,
      padding: "2px 6px",
      borderRadius: 4,
      fontSize: 11,
      color: p.accent,
      fontFamily: "monospace",
    },
    pdfBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 16px",
      background: p.bgCard,
      border: `1px solid ${p.border}`,
      borderRadius: 10,
      color: p.text,
      textDecoration: "none",
      fontSize: 13,
      fontWeight: 600,
      transition: "all 120ms",
      cursor: "pointer",
      fontFamily: "inherit",
    },
    pdfRow: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      marginTop: 10,
    },
    dataSheetBand: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      background: "rgba(0,0,0,0.92)",
      color: "#fff",
      padding: "10px 14px",
      borderRadius: 8,
      fontFamily: "monospace",
    },
    dataSheetBandLabel: {
      fontSize: 10,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      opacity: 1,
      color: "rgba(255,255,255,0.9)",
    },
    dataSheetBandValue: {
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 0.5,
    },
    dataSheetOverlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.92)",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      zIndex: 2000,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: 32,
      cursor: "zoom-out",
    },
    dataSheetClose: {
      position: "absolute",
      top: 16,
      right: 16,
      width: 44,
      height: 44,
      background: "rgba(255,255,255,0.1)",
      border: "1px solid rgba(255,255,255,0.2)",
      color: "#fff",
      borderRadius: 10,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    dataSheetOverlayBand: {
      position: "absolute",
      top: 24,
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      alignItems: "center",
      gap: 14,
      background: "rgba(0,0,0,0.95)",
      color: "#fff",
      padding: "10px 18px",
      borderRadius: 8,
      fontFamily: "monospace",
      border: "1px solid rgba(255,255,255,0.2)",
    },
    dataSheetOverlayBandLabel: {
      fontSize: 10,
      letterSpacing: 1.2,
      textTransform: "uppercase",
      opacity: 1,
      color: "rgba(255,255,255,0.9)",
    },
    dataSheetOverlayBandValue: {
      fontSize: 14,
      fontWeight: 700,
      letterSpacing: 0.5,
    },
    dataSheetIframe: {
      width: "min(960px, 92vw)",
      height: "min(80vh, 800px)",
      border: "1px solid rgba(255,255,255,0.15)",
      borderRadius: 10,
      background: "#1a1a1a",
      boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
      cursor: "default",
    },
    footer: {
      display: "flex",
      gap: 12,
      padding: "1rem 1.5rem",
      borderTop: `1px solid ${p.border}`,
      background: p.bg,
      flexShrink: 0,
    },
    qtyInput: {
      flex: "0 0 130px",
      padding: "10px 14px",
      background: p.inputBg,
      border: `1px solid ${p.border}`,
      borderRadius: 10,
      color: p.text,
      fontSize: 14,
      fontFamily: "inherit",
      outline: "none",
    },
    addBtn: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: "10px 20px",
      background: p.accent,
      color: p.accentFg,
      border: "none",
      borderRadius: 10,
      fontSize: 14,
      fontWeight: 600,
      fontFamily: "inherit",
      transition: "all 120ms",
    },
  };
}
